/**
 * Acquisition attribution capture.
 *
 * Ad clicks land on the app with UTM params + `fbclid` already in the URL — the
 * landing page (carbackground.ai) forwards them onto every app.carbackground.ai
 * link. The app is a different origin, so the landing's localStorage does not
 * carry over: the URL is the handoff. We must read the params on arrival, before
 * passwordless auth navigation drops them (OAuth returns to `/`, OTP goes to
 * `/otp`). Captured values are persisted first-touch on the user's profile at
 * sign-in (see auth-provider) and propagated to Stripe / PostHog.
 */

import { getCookie } from "@/lib/cookies";

/** URL/localStorage keys captured verbatim from the ad-click URL. */
const ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
] as const;

type AttributionParamKey = (typeof ATTRIBUTION_PARAM_KEYS)[number];

/** Shape persisted to the profile / sent to Stripe + PostHog. */
export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  /** Meta click id (fbc cookie, or derived from fbclid). */
  fbc?: string;
  /** Meta browser id (fbp cookie). */
  fbp?: string;
}

/**
 * Read attribution params from the current URL and persist them to app-origin
 * localStorage. First-touch: never clobber an already-stored value, so the
 * original acquisition source survives in-app navigation and repeat visits.
 * Cheap, synchronous, idempotent — safe to call on every app boot.
 */
export function captureAttributionFromUrl(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = params.get(key);
    if (value && !localStorage.getItem(key)) {
      localStorage.setItem(key, value);
    }
  }
}

/**
 * Collect the stored attribution: UTMs + fbclid from localStorage, fbp from its
 * cookie, and fbc from the `_fbc` cookie when present (otherwise derived from
 * fbclid in Meta's `fb.1.<ts>.<fbclid>` format). Empty values are omitted.
 */
export function getStoredAttribution(): Attribution {
  if (typeof window === "undefined") return {};

  const result: Attribution = {};

  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = localStorage.getItem(key);
    if (value) result[key as AttributionParamKey] = value;
  }

  const fbp = getCookie("_fbp");
  if (fbp) result.fbp = fbp;

  const fbcCookie = getCookie("_fbc");
  if (fbcCookie) {
    result.fbc = fbcCookie;
  } else if (result.fbclid) {
    result.fbc = `fb.1.${Date.now()}.${result.fbclid}`;
  }

  return result;
}
